<template>
  <div class="shadow-md">
  <header class="navbar bg-[#f9f5f0] min-h-0 py-2">
    <div class="flex-none lg:hidden">
      <slot name="hamburger"></slot>
    </div>
    <div class="flex-1">
      <span class="text-xl font-bold text-white">
        <img
          src="/images/reengki_text_logo.png"
          alt="Reengki Text Logo"
          class="pl-2 h-6 w-auto object-contain"
        />
      </span>
    </div>
    <div class="flex-none gap-2 flex items-center">
      <!-- 전환 모드 복귀 배너 -->
      <button
        v-if="isSwitched"
        class="btn btn-sm btn-warning gap-1"
        @click="returnToRoot"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
        </svg>
        <span class="hidden sm:inline">관리자로 돌아가기</span>
      </button>

      <!-- 계정 전환 버튼 (root만) -->
      <button
        v-if="isRoot && !isSwitched"
        class="btn btn-sm btn-outline gap-1"
        @click="openSwitchModal"
        title="계정 전환"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
        <span class="hidden sm:inline">계정 전환</span>
      </button>

      <!-- 인증 정보 텍스트 -->
      <div v-if="userInfo" class="ml-4 text-right leading-tight">
        <div class="text-lg font-bold text-gray-800">{{ displayName }}</div>
        <div class="text-xs text-gray-500">{{ userInfo }}님</div>
        <div v-if="summary" class="flex flex-nowrap justify-end items-center gap-1 mt-1">
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-100 whitespace-nowrap">💰 일반 {{ n(summary.common) }}P</span>
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-100 whitespace-nowrap">🎁 프로모션 {{ n(summary.promotion) }}P</span>
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">🎫 틴즈 {{ n(summary.teenz) }}장</span>
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">🔤 파닉스 {{ n(summary.phonics) }}장</span>
        </div>
      </div>
      <!-- 로그아웃 버튼 -->
      <button
        class="btn btn-ghost btn-circle"
        @click="showLogoutConfirm = true"
        title="로그아웃"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"
          />
        </svg>
      </button>
    </div>

    <!-- 로그아웃 확인 모달 -->
    <div v-if="showLogoutConfirm" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg">로그아웃 하시겠습니까?</h3>
        <div class="modal-action">
          <button class="btn btn-primary" @click="logout">확인</button>
          <button class="btn" @click="showLogoutConfirm = false">
            취소
          </button>
        </div>
      </div>
    </div>

    <!-- 계정 전환 모달 -->
    <div v-if="showSwitchModal" class="modal modal-open">
      <div class="modal-box max-w-2xl h-[90vh] flex flex-col !overflow-hidden">
        <h3 class="font-bold text-lg mb-4">계정 전환</h3>

        <!-- 검색 -->
        <input
          v-model="switchSearch"
          type="text"
          placeholder="지사명 또는 학원명 검색..."
          class="input input-bordered w-full mb-4"
          @input="switchSearch = ($event.target as HTMLInputElement).value"
        />

        <!-- 로딩 -->
        <div v-if="switchLoading" class="flex justify-center py-8">
          <span class="loading-spinner w-8 h-8"></span>
        </div>

        <!-- 에러 -->
        <div v-else-if="switchError" class="text-red-500 text-sm py-4 text-center">
          {{ switchError }}
        </div>

        <!-- 목록 -->
        <div v-else class="flex-1 overflow-y-auto">
          <div
            v-for="agency in filteredSwitchList"
            :key="agency.AgencyID"
            class="mb-2"
          >
            <!-- 지사 헤더 -->
            <div
              class="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg border border-gray-200 cursor-pointer hover:bg-gray-100"
              @click="switchToAccount('agency', agency.AgencyID, agency.AgencyName)"
            >
              <div class="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span class="font-semibold text-sm">{{ agency.AgencyName }}</span>
              </div>
              <span class="badge badge-sm badge-info">지사</span>
            </div>
            <!-- 소속 학원 -->
            <div
              v-for="academy in agency.academies"
              :key="academy.AcademyID"
              class="flex items-center justify-between px-3 py-2 pl-8 border-x border-b border-gray-200 cursor-pointer hover:bg-[#1ecad3]/5 last:rounded-b-lg"
              @click="switchToAccount('academy', academy.AcademyID, academy.AcademyName, agency.AgencyID, agency.AgencyName)"
            >
              <div class="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span class="text-sm">{{ academy.AcademyName || academy.AcademyID }}</span>
              </div>
              <span class="badge badge-sm badge-success">학원</span>
            </div>
          </div>

          <div
            v-if="filteredSwitchList.length === 0 && !switchLoading"
            class="text-gray-400 text-sm text-center py-8"
          >
            검색 결과가 없습니다.
          </div>
        </div>

        <!-- 전환 중 표시 -->
        <div v-if="switching" class="flex items-center gap-2 mt-4 text-sm text-gray-500">
          <span class="loading-spinner w-4 h-4"></span>
          계정 전환 중...
        </div>

        <div class="modal-action">
          <button class="btn" @click="showSwitchModal = false" :disabled="switching">닫기</button>
        </div>
      </div>
    </div>
  </header>
  <!-- 소속 정보 서브바 -->
  <div v-if="accountType === 'academy' || accountType === 'agency'" class="bg-[#f3ede6] px-4 py-1 flex items-center justify-between text-xs text-gray-600 border-t border-[#e8dfd5]">
    <div class="flex items-center gap-2">
      <span v-if="agencyName" class="text-gray-400">{{ agencyName }}</span>
      <span v-if="agencyName && academyName" class="text-gray-300">/</span>
      <span v-if="academyName" class="font-medium text-gray-700">{{ academyName }}</span>
    </div>
    <span class="badge badge-outline badge-xs text-gray-500 border-gray-400">{{ accountType }}</span>
  </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import fetchWithAuth from "../utils/fetchWithAuth";

const router = useRouter();
const emit = defineEmits<{ accountSwitched: [] }>();

const showLogoutConfirm = ref(false);

// 인증 정보
const userInfo = ref<string | null>(null);
const agencyName = ref<string | null>(null);
const academyName = ref<string | null>(null);
const accountType = ref<string | null>(null);

const displayName = computed(() => {
  return academyName.value && academyName.value !== ""
    ? academyName.value
    : agencyName.value ?? "";
});

const isRoot = computed(() => accountType.value === "root");
const isSwitched = computed(() => !!localStorage.getItem("rootToken"));

// === 계정 전환 ===
interface SwitchAcademy {
  AcademyID: number;
  AcademyName: string;
}
interface SwitchAgency {
  AgencyID: number;
  AgencyName: string;
  academies: SwitchAcademy[];
}

const showSwitchModal = ref(false);
const switchLoading = ref(false);
const switchError = ref("");
const switchSearch = ref("");
const switching = ref(false);
const agencyTreeData = ref<SwitchAgency[]>([]);

const filteredSwitchList = computed(() => {
  const q = switchSearch.value.toLowerCase().trim();
  if (!q) return agencyTreeData.value;
  return agencyTreeData.value
    .map((agency) => {
      const agencyMatch = agency.AgencyName.toLowerCase().includes(q);
      const matchedAcademies = agency.academies.filter(
        (a) => (a.AcademyName || "").toLowerCase().includes(q) || String(a.AcademyID).includes(q)
      );
      if (agencyMatch) return agency; // 지사명 매칭이면 전체 표시
      if (matchedAcademies.length > 0) return { ...agency, academies: matchedAcademies };
      return null;
    })
    .filter(Boolean) as SwitchAgency[];
});

async function openSwitchModal() {
  showSwitchModal.value = true;
  switchSearch.value = "";
  switchError.value = "";
  if (agencyTreeData.value.length > 0) return;
  switchLoading.value = true;
  try {
    const res = await fetchWithAuth(
      import.meta.env.VITE_API_URL + "/api/learning-history/get-agencies-with-academies",
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      },
    );
    if (!res || !res.ok) throw new Error("목록을 불러올 수 없습니다.");
    const payload = await res.json();
    const list = payload?.agencies ?? payload?.data;
    if (!payload?.result || !Array.isArray(list)) {
      throw new Error("응답 형식이 올바르지 않습니다.");
    }
    agencyTreeData.value = list
      .map((agency: any) => ({
        AgencyID: Number(agency.AgencyID),
        AgencyName: String(agency.AgencyName ?? ""),
        academies: Array.isArray(agency.Academies ?? agency.academies)
          ? (agency.Academies ?? agency.academies)
              .map((a: any) => ({
                AcademyID: Number(a.AcademyID),
                AcademyName: String(a.AcademyName ?? ""),
              }))
              .filter((a: SwitchAcademy) => Number.isFinite(a.AcademyID) && a.AcademyID > 0)
          : [],
      }))
      .filter((a: SwitchAgency) => Number.isFinite(a.AgencyID) && a.AgencyID > 0);
  } catch (e: any) {
    switchError.value = e.message || "목록 로딩 실패";
  } finally {
    switchLoading.value = false;
  }
}

async function switchToAccount(
  type: "agency" | "academy",
  targetId: number,
  targetName: string,
  parentAgencyId?: number,
  parentAgencyName?: string,
) {
  if (switching.value) return;
  switching.value = true;
  try {
    const res = await fetchWithAuth(
      import.meta.env.VITE_API_URL + "/api/lms/switch-account",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          targetType: type,
          targetId,
        }),
      },
    );
    if (!res || !res.ok) {
      const errBody = res ? await res.json().catch(() => null) : null;
      throw new Error(errBody?.message || "계정 전환에 실패했습니다.");
    }
    const data = await res.json();
    if (!data.result || !data.access_token) {
      throw new Error(data.message || "계정 전환 응답이 올바르지 않습니다.");
    }

    // root 토큰 백업 (최초 전환 시만)
    if (!localStorage.getItem("rootToken")) {
      localStorage.setItem("rootToken", localStorage.getItem("token") || "");
      localStorage.setItem("rootUserName", localStorage.getItem("userName") || "");
      localStorage.setItem("rootAccountType", localStorage.getItem("accountType") || "");
      localStorage.setItem("rootAgencyName", localStorage.getItem("agencyName") || "");
      localStorage.setItem("rootAcademyName", localStorage.getItem("academyName") || "");
      localStorage.setItem("rootAgencyID", localStorage.getItem("agencyID") || "");
      localStorage.setItem("rootAcademyID", localStorage.getItem("academyID") || "");
      localStorage.setItem("rootAccount", localStorage.getItem("account") || "");
    }

    // 전환된 계정 정보로 덮어쓰기
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("accountType", type);
    if (data.auth_info) {
      localStorage.setItem("userName", data.auth_info.OwnerName ?? targetName);
      localStorage.setItem("agencyName", data.auth_info.agency_name ?? parentAgencyName ?? "");
      localStorage.setItem("academyName", data.auth_info.academy_name ?? (type === "academy" ? targetName : ""));
      localStorage.setItem("agencyID", String(data.auth_info.AgencyID ?? parentAgencyId ?? targetId));
      localStorage.setItem("academyID", String(data.auth_info.AcademyID ?? (type === "academy" ? targetId : "")));
      localStorage.setItem("account", data.auth_info.Account ?? "");
    } else {
      localStorage.setItem("userName", targetName);
      localStorage.setItem("agencyName", type === "agency" ? targetName : (parentAgencyName ?? ""));
      localStorage.setItem("academyName", type === "academy" ? targetName : "");
      localStorage.setItem("agencyID", String(type === "agency" ? targetId : (parentAgencyId ?? "")));
      localStorage.setItem("academyID", String(type === "academy" ? targetId : ""));
    }

    // UI 갱신
    refreshAuthState();
    showSwitchModal.value = false;
    emit("accountSwitched");
    // 현재 페이지 새로고침하여 전환된 계정 데이터 반영
    router.go(0);
  } catch (e: any) {
    switchError.value = e.message || "전환 실패";
  } finally {
    switching.value = false;
  }
}

function returnToRoot() {
  const rootToken = localStorage.getItem("rootToken");
  if (!rootToken) return;

  // root 정보 복원
  localStorage.setItem("token", rootToken);
  localStorage.setItem("userName", localStorage.getItem("rootUserName") || "");
  localStorage.setItem("accountType", localStorage.getItem("rootAccountType") || "root");
  localStorage.setItem("agencyName", localStorage.getItem("rootAgencyName") || "");
  localStorage.setItem("academyName", localStorage.getItem("rootAcademyName") || "");
  localStorage.setItem("agencyID", localStorage.getItem("rootAgencyID") || "");
  localStorage.setItem("academyID", localStorage.getItem("rootAcademyID") || "");
  localStorage.setItem("account", localStorage.getItem("rootAccount") || "");

  // 백업 키 제거
  localStorage.removeItem("rootToken");
  localStorage.removeItem("rootUserName");
  localStorage.removeItem("rootAccountType");
  localStorage.removeItem("rootAgencyName");
  localStorage.removeItem("rootAcademyName");
  localStorage.removeItem("rootAgencyID");
  localStorage.removeItem("rootAcademyID");
  localStorage.removeItem("rootAccount");

  refreshAuthState();
  agencyTreeData.value = []; // 캐시 초기화
  emit("accountSwitched");
  router.go(0);
}

function refreshAuthState() {
  userInfo.value = localStorage.getItem("userName");
  agencyName.value = localStorage.getItem("agencyName");
  academyName.value = localStorage.getItem("academyName");
  accountType.value = localStorage.getItem("accountType");
}

// 상단바 요약: 일반/프로모션 포인트 + 틴즈/파닉스 보유 이용권 (원장 계정만)
const summary = ref<{ common: number; promotion: number; teenz: number; phonics: number } | null>(null);
const n = (v: number) => Number(v || 0).toLocaleString();
async function loadSummary() {
  if (localStorage.getItem("accountType") !== "academy") { summary.value = null; return; }
  const API = import.meta.env.VITE_API_URL ?? "";
  const opts = { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } };
  try {
    const [pRes, tRes, phRes] = await Promise.all([
      fetchWithAuth(`${API}/api/admin/order/mypoint`, opts),
      fetchWithAuth(`${API}/api/admin/afl-academies-hodoo/getAvailableTicket/BETIACLASS`, opts),
      fetchWithAuth(`${API}/api/admin/afl-academies-hodoo/getAvailableTicket/STARLAND`, opts),
    ]);
    const p = pRes?.ok ? await pRes.json() : {};
    const t = tRes?.ok ? await tRes.json() : {};
    const ph = phRes?.ok ? await phRes.json() : {};
    summary.value = {
      common: Number(p.myPoint || 0),
      promotion: Number(p.promotionPoint || 0),
      teenz: Number(t.issues_ticket_count || 0),
      phonics: Number(ph.issues_phonics_ticket_count || 0),
    };
  } catch (e) {
    console.error("상단 요약 로드 실패:", e);
  }
}

onMounted(() => {
  refreshAuthState();
  loadSummary();
});

function logout() {
  // 전환 모드 백업 데이터도 정리
  localStorage.removeItem("rootToken");
  localStorage.removeItem("rootUserName");
  localStorage.removeItem("rootAccountType");
  localStorage.removeItem("rootAgencyName");
  localStorage.removeItem("rootAcademyName");
  localStorage.removeItem("rootAgencyID");
  localStorage.removeItem("rootAcademyID");
  localStorage.removeItem("rootAccount");

  localStorage.removeItem("token");
  localStorage.removeItem("userName");
  localStorage.removeItem("agencyName");
  localStorage.removeItem("academyName");
  localStorage.removeItem("accountType");
  localStorage.removeItem("agencyID");
  localStorage.removeItem("academyID");
  localStorage.removeItem("account");
  showLogoutConfirm.value = false;
  router.push("/signin");
}
</script>
